import * as React from "react";
import { Context } from "@grandvisionhq/carts-v2";
import { Loader, Modal } from "@grandvisionhq/common";
import { Alert } from "@grandvisionhq/iris-ui";
import { useIntl, useFeatureToggles } from "@grandvisionhq/state";
import { useDynamicModule } from "@grandvisionhq/utils";
import { pushLoginExpressEditUserDetailsClickEventToDataLayer } from "../../../../../../../../../../data-layer.js";
import { useSetServiceStore } from "../../../../../../../../../../hooks/operations/hooks/operations/use-set-service-store.js";
import { StoreFinderItemContainer } from "../../../../../../../../../../modules/ServiceStore/containers/modules/ServiceStore/containers/StoreFinderItemContainer.js";
import { ServiceStoreSummaryContainer } from "./ServiceStoreSummaryContainer.js";
import { useLoggedInCheckout } from "../../../../../../../../../context/LoginExpressCheckout/context/context.js";
const loadModule = async () => {
  const storesModule = await import("@grandvisionhq/stores-v2");
  return storesModule;
};
const usePickupStoresModule = () => useDynamicModule(loadModule, "@grandvisionhq/stores-v2", []);
const ServiceStore = ({
  globalStoreId,
  title,
  isServiceStoreValid,
  isOrderDetailsValid
}) => {
  const { cart } = Context.useCart();
  const [pickupStoresModule, isLoadingPickupStores, pickUpStoreModuleError] = usePickupStoresModule();
  const { getLabel } = useIntl();
  const featuresToggles = useFeatureToggles();
  const { isUserLoggedIn } = useLoggedInCheckout();
  const [showModal, setShowModal] = React.useState(false);
  const { setServiceStore } = useSetServiceStore();
  const labels = React.useMemo(
    () => ({
      title: getLabel("checkout.serviceStore.title"),
      subtitle: getLabel("checkout.serviceStore.subtitle"),
      errorMessage: getLabel("checkout.serviceStore.error")
    }),
    [getLabel]
  );
  const handleStoreFinder = React.useCallback(
    async (globalStoreId2) => {
      if (!globalStoreId2) return;
      const shouldFetchStore = cart && !cart?.serviceStore;
      if (shouldFetchStore) {
        await setServiceStore(globalStoreId2);
      }
    },
    [cart, setServiceStore]
  );
  React.useEffect(() => {
    handleStoreFinder(globalStoreId);
  }, [globalStoreId]);
  const onStoreSelect = React.useCallback(
    async (store) => {
      if (store?.globalStoreId) {
        await setServiceStore(store?.globalStoreId);
      }
      setShowModal(false);
    },
    [setServiceStore]
  );
  const onEditHandler = React.useCallback(() => {
    pushLoginExpressEditUserDetailsClickEventToDataLayer({
      editType: isServiceStoreValid ? "edit_service_store" : "add_service_store_alert",
      isLoggedIn: isUserLoggedIn
    });
    setShowModal(true);
  }, [isServiceStoreValid, isUserLoggedIn]);
  if (pickUpStoreModuleError || !pickupStoresModule) {
    return /* @__PURE__ */ React.createElement(Alert, { variant: "error", notifyScreenReader: false, className: "alert-m-default" }, labels.errorMessage);
  }
  const { Components, Containers, StoreFinderProvider } = pickupStoresModule;
  const { StoreFinder, PickUpButtons, withPickUpButtonsConfig } = Containers;
  const { StoreListTopContent } = Components;
  if (isLoadingPickupStores) {
    return /* @__PURE__ */ React.createElement(Loader, null);
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    Modal,
    {
      className: "service-store__modal",
      showIcon: true,
      iconClassName: "service-store__modal__close",
      isOpen: showModal,
      onModalClose: () => setShowModal(false)
    },
    /* @__PURE__ */ React.createElement(
      StoreFinderProvider,
      {
        googleMapsId: "google-maps-service-store",
        hidePhone: !featuresToggles.isStorePickupPhoneEnabled,
        hideEmail: !featuresToggles.isStorePickupEmailEnabled
      },
      /* @__PURE__ */ React.createElement(
        StoreFinder,
        {
          isInsideModal: true,
          listTopContent: /* @__PURE__ */ React.createElement(StoreListTopContent, { title: labels.title, description: labels.subtitle }),
          listItemComponent: (props) => /* @__PURE__ */ React.createElement(
            StoreFinderItemContainer,
            {
              ...props,
              variant: "list-item",
              onClick: onStoreSelect
            }
          ),
          infoWindowContentComponent: (props) => /* @__PURE__ */ React.createElement(StoreFinderItemContainer, { ...props, variant: "map-item", onClick: onStoreSelect }),
          buttonsComponent: withPickUpButtonsConfig(PickUpButtons, {
            onStoreSelect
          })
        }
      )
    )
  ), /* @__PURE__ */ React.createElement(
    ServiceStoreSummaryContainer,
    {
      isOrderDetailsValid: Boolean(isOrderDetailsValid),
      isServiceStoreValid: Boolean(isServiceStoreValid),
      title,
      onEditHandler
    }
  ));
};
export {
  ServiceStore
};
//# sourceMappingURL=ServiceStore.js.map
